import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthToken, useCurrentUser } from '@mystudioclass/auth';
import { Video, User, Phone, Building2 } from '../components/Icons';
import toast from 'react-hot-toast';

// API_URL should include /api suffix (same as api.js)
const API_URL = import.meta.env.VITE_API_URL || 'https://studioscale-backend-production.up.railway.app/api';

export default function CompleteProfilePage() {
  const { getToken } = useAuthToken();
  const { user: authUser } = useCurrentUser();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    telephone: '',
    studioId: ''
  });
  const [loading, setLoading] = useState(false);
  const [studioIdFromQR, setStudioIdFromQR] = useState(false);

  // Force light mode for this page
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      }
    };
  }, []);

  // Load studioId from URL params or session storage (from public schedule page)
  useEffect(() => {
    // Priority: URL param > sessionStorage
    const studioIdFromUrl = searchParams.get('studioId');
    const pendingStudioId = sessionStorage.getItem('pendingStudioId');

    const studioId = studioIdFromUrl || pendingStudioId;

    if (studioId) {
      setFormData(prev => ({ ...prev, studioId }));
      setStudioIdFromQR(true); // Mark as pre-filled from QR code
      // Clear from sessionStorage after using it
      if (pendingStudioId) {
        sessionStorage.removeItem('pendingStudioId');
      }
    }
  }, [searchParams]);

  // Pre-fill name from Better Auth if available
  useEffect(() => {
    if (authUser) {
      setFormData(prev => ({
        ...prev,
        firstName: authUser.firstName || prev.firstName,
        lastName: authUser.lastName || prev.lastName
      }));
    }
  }, [authUser]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Phone number formatter
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (!phoneNumber) return '';

    const hasCountryCode = phoneNumber.length === 11 && phoneNumber[0] === '1';
    const digits = hasCountryCode ? phoneNumber.slice(1) : phoneNumber;

    if (digits.length < 4) {
      return hasCountryCode ? `+1 (${digits}` : `(${digits}`;
    } else if (digits.length < 7) {
      return hasCountryCode
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return hasCountryCode
        ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, telephone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName) {
      toast.error('Please enter your name');
      return;
    }

    if (!formData.telephone) {
      toast.error('Please enter your phone number');
      return;
    }

    if (!formData.studioId) {
      toast.error('Please enter a studio ID');
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();

      // Complete profile with the backend
      const response = await fetch(`${API_URL}/profile/complete-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.telephone,
          role: 'student',
          studioId: formData.studioId,
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Profile completed! Welcome to My Studio Class.');
        // Check for returnTo param and navigate there, otherwise go to dashboard
        const returnTo = searchParams.get('returnTo');
        if (returnTo) {
          window.location.href = decodeURIComponent(returnTo);
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        toast.error(data.message || 'Failed to complete registration');
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      toast.error('Failed to complete profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--bg-base)' }}
    >
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              boxShadow: '0 0 40px var(--primary-30)'
            }}
          >
            <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Complete Your Profile
          </h1>
          <p style={{ color: 'var(--text-muted)' }} className="mt-2">
            Tell us a bit about yourself.
          </p>
        </div>

        {/* Profile Completion Form */}
        <div
          className="rounded-2xl shadow-xl p-8"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-muted)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  <User className="w-4 h-4 inline mr-1" style={{ color: 'var(--primary)' }} />
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg transition-colors"
                  style={{
                    background: 'var(--bg-muted)',
                    border: '1px solid var(--border-muted)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: 16 // Prevents iOS zoom on focus
                  }}
                  placeholder="John"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg transition-colors"
                  style={{
                    background: 'var(--bg-muted)',
                    border: '1px solid var(--border-muted)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: 16 // Prevents iOS zoom on focus
                  }}
                  placeholder="Doe"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                <Phone className="w-4 h-4 inline mr-1" style={{ color: 'var(--primary)' }} />
                Phone Number
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handlePhoneChange}
                className="w-full px-3 py-2.5 rounded-lg transition-colors"
                style={{
                  background: 'var(--bg-muted)',
                  border: '1px solid var(--border-muted)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  fontSize: 16 // Prevents iOS zoom on focus
                }}
                placeholder="(555) 123-4567"
                maxLength={17}
                required
                disabled={loading}
              />
            </div>

            {/* Studio ID */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                <Building2 className="w-4 h-4 inline mr-1" style={{ color: 'var(--primary)' }} />
                Studio ID
              </label>
              <input
                type="text"
                name="studioId"
                value={formData.studioId}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg transition-colors"
                style={{
                  background: studioIdFromQR ? 'var(--bg-base)' : 'var(--bg-muted)',
                  border: '1px solid var(--border-muted)',
                  color: studioIdFromQR ? 'var(--text-muted)' : 'var(--text-primary)',
                  outline: 'none',
                  cursor: studioIdFromQR ? 'not-allowed' : 'text',
                  fontSize: 16 // Prevents iOS zoom on focus
                }}
                placeholder="Enter the studio ID from your studio"
                required
                disabled={loading || studioIdFromQR}
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                {studioIdFromQR
                  ? 'Studio ID pre-filled from your scanned QR code.'
                  : 'Ask your studio for this ID to connect your account.'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition-all"
              style={{
                background: loading
                  ? 'var(--text-muted)'
                  : 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                color: 'white',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 12px var(--primary-30)'
              }}
            >
              {loading ? 'Completing Profile...' : 'Complete Registration'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
